import { logger } from '../utils/logger.js';
import { createApiError, dispatchApiErrorEvent } from './apiError';
import type { ApiError } from './apiError';
import { resolveApiBaseForPath } from './requestRouting';

type RequestHeaders = Record<string, string>;

interface RefreshResult {
  accessToken?: string;
}

interface RequestClientConfig {
  onSessionExpired?: () => void;
  refreshAccessToken?: (expiredToken: string) => Promise<RefreshResult | null | undefined>;
}

let globalRequestClientConfig: RequestClientConfig = {};

export const setGlobalRequestClientConfig = (config: RequestClientConfig = {}): void => {
  globalRequestClientConfig = { ...config };
};

type PortalRequestOptions = Omit<RequestInit, 'headers'> & {
  isFileUpload?: boolean;
  requestName?: string;
  headers?: RequestHeaders;
};

type PortalRequest = <T = unknown>(path: string, options?: PortalRequestOptions) => Promise<T>;

const LOG_MODULE = 'portalApiClient';
const RETRY_DELAY_MS = 800;
const RETRY_LIMIT = 1;
const AUTH_REFRESH_BLOCKLIST = ['/login', '/refresh', '/logout', '/register', '/forgot-password', '/verify-email-otp'];

const attemptAccessTokenRefresh = async (
  config: RequestClientConfig = {},
  expiredToken = ''
): Promise<string> => {
  if (typeof config.refreshAccessToken !== 'function') {
    return '';
  }
  try {
    const refreshed = await config.refreshAccessToken(expiredToken);
    return refreshed?.accessToken || '';
  } catch (error) {
    return '';
  }
};

const sleep = (delay: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, delay);
});

const normalizeError = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack
    };
  }
  return {
    value: String(error)
  };
};

const shouldRetryForStatus = (status: number): boolean => status === 0 || status >= 500;

const extractApiErrorMessage = (data: unknown, fallback = 'Request failed'): string => {
  if (!data || typeof data !== 'object') {
    return fallback;
  }
  const candidate = (data as { message?: unknown }).message;
  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim();
  }
  return fallback;
};

const shouldAttemptUnauthorizedRefresh = (
  path: string,
  credentials: RequestCredentials | undefined,
  hasRefreshHandler: boolean
): boolean => {
  if (!hasRefreshHandler) {
    return false;
  }
  if (AUTH_REFRESH_BLOCKLIST.some((fragment) => path.includes(fragment))) {
    return false;
  }
  return credentials === 'include';
};

const dispatchApiLifecycleEvent = (
  type: 'api-request-start' | 'api-request-end',
  detail: Record<string, unknown>
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(type, { detail }));
};

const createRequest = (apiBase = '', config: RequestClientConfig = {}): PortalRequest => async <T = unknown>(
  path: string,
  options: PortalRequestOptions = {}
): Promise<T> => {
  const effectiveConfig: RequestClientConfig = {
    ...globalRequestClientConfig,
    ...config
  };
  const requestName = options.requestName;
  const requestId = `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const headers: RequestHeaders = {
    ...(!options.isFileUpload && { 'Content-Type': 'application/json' }),
    ...(options.headers || {}),
    ...(requestName ? { 'X-Request-Name': requestName } : {})
  };
  const effectiveHeaders = headers;
  const resolvedApiBase = resolveApiBaseForPath(apiBase, path);
  const requestPath = requestName
    ? `${path}${path.includes('?') ? '&' : '?'}__req=${encodeURIComponent(requestName)}`
    : path;
  const finalOptions: RequestInit = {
    ...(options as RequestInit),
    credentials: options.credentials ?? 'include',
    headers: effectiveHeaders
  };
  const requestUrl = `${resolvedApiBase}${requestPath}`;

  logger.debug(LOG_MODULE, 'Request started', {
    source: { file: 'shared/api/portalApiClient.ts', method: 'createRequest' },
    path,
    requestName,
    method: finalOptions.method || 'GET'
  });

  dispatchApiLifecycleEvent('api-request-start', {
    requestId,
    requestName,
    path,
    method: finalOptions.method || 'GET'
  });

  let finalStatus = 0;
  let finalOk = false;
  let finalAborted = false;

  const doFetch = async (): Promise<Response> => {
    let attempt = 0;

    while (attempt <= RETRY_LIMIT) {
      try {
        const response = await fetch(requestUrl, finalOptions);

        if (attempt < RETRY_LIMIT && shouldRetryForStatus(response.status)) {
          logger.warn(LOG_MODULE, 'Retrying request after retryable status', {
            source: { file: 'shared/api/portalApiClient.ts', method: 'doFetch' },
            path,
            requestName,
            status: response.status,
            attempt: attempt + 1
          });
          attempt += 1;
          await sleep(RETRY_DELAY_MS);
          continue;
        }
        return response;
      } catch (error) {
        const errorName = (error && typeof error === 'object' && 'name' in error)
          ? String((error as { name?: unknown }).name || '')
          : '';
        if (errorName === 'AbortError') {
          finalAborted = true;
          dispatchApiErrorEvent({ error, requestName, path });
          logger.warn(LOG_MODULE, 'Request aborted', {
            source: { file: 'shared/api/portalApiClient.ts', method: 'doFetch' },
            path,
            requestName
          });
          throw error;
        }
        if (attempt < RETRY_LIMIT) {
          logger.warn(LOG_MODULE, 'Retrying request after network failure', {
            source: { file: 'shared/api/portalApiClient.ts', method: 'doFetch' },
            path,
            requestName,
            attempt: attempt + 1,
            error: normalizeError(error)
          });
          attempt += 1;
          await sleep(RETRY_DELAY_MS);
          continue;
        }
        dispatchApiErrorEvent({ error, requestName, path });
        logger.error(LOG_MODULE, 'Request failed before receiving response', {
          source: { file: 'shared/api/portalApiClient.ts', method: 'doFetch' },
          path,
          requestName,
          error: normalizeError(error)
        });
        throw error;
      }
    }

    const unexpectedError: ApiError = createApiError({
      message: 'Request failed',
      status: 0,
      requestName,
      path
    });
    dispatchApiErrorEvent({ error: unexpectedError, requestName, path });
    throw unexpectedError;
  };

  let response: Response | null = null;
  try {
    response = await doFetch();
    finalStatus = response.status;
    finalOk = response.ok;

    if (response.status === 401 && shouldAttemptUnauthorizedRefresh(
      path,
      finalOptions.credentials,
      typeof effectiveConfig.refreshAccessToken === 'function'
    )) {
      const nextAccessToken = await attemptAccessTokenRefresh(effectiveConfig, '');
      if (nextAccessToken) {
        response = await doFetch();
        finalStatus = response.status;
        finalOk = response.ok;
      }
    }

    if (response.status === 401 && typeof effectiveConfig.onSessionExpired === 'function') {
      effectiveConfig.onSessionExpired();
    }

    const text = await response.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        if (response.ok) {
          data = text;
          logger.warn(LOG_MODULE, 'Failed to parse success response JSON; returning raw payload', {
            source: { file: 'shared/api/portalApiClient.ts', method: 'createRequest' },
            path,
            requestName,
            status: response.status,
            error: normalizeError(parseError)
          });
        } else {
          data = {
            raw: text
          };
          logger.warn(LOG_MODULE, 'Failed to parse error response JSON', {
            source: { file: 'shared/api/portalApiClient.ts', method: 'createRequest' },
            path,
            requestName,
            status: response.status,
            error: normalizeError(parseError)
          });
        }
      }
    }

    if (!response.ok) {
      const error: ApiError = createApiError({
        message: extractApiErrorMessage(data, 'Request failed'),
        status: response.status,
        data,
        requestName,
        path
      });

      logger.error(LOG_MODULE, 'Request failed', {
        source: { file: 'shared/api/portalApiClient.ts', method: 'createRequest' },
        path,
        requestName,
        status: response.status,
        data
      });
      dispatchApiErrorEvent({ error, requestName, path });
      throw error;
    }

    logger.info(LOG_MODULE, 'Request succeeded', {
      source: { file: 'shared/api/portalApiClient.ts', method: 'createRequest' },
      path,
      requestName,
      status: response.status
    });
    return data as T;
  } finally {
    dispatchApiLifecycleEvent('api-request-end', {
      requestId,
      requestName,
      path,
      status: finalStatus,
      ok: finalOk,
      aborted: finalAborted
    });
  }
};

export default createRequest;
